// @ts-nocheck
/**
 * v0 by Vercel.
 * @see https://v0.dev/t/ONaXRLWB7TZ
 * Documentation: https://v0.dev/docs#integrating-generated-code-into-your-nextjs-app
 */
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { DialogTrigger, DialogTitle, DialogDescription, DialogHeader, DialogFooter, DialogContent, Dialog } from "@/components/ui/dialog"
import { CardTitle, CardDescription, CardHeader, CardContent, Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem, DropdownMenuContent, DropdownMenu } from "@/components/ui/dropdown-menu"
import { DrawerTrigger, DrawerTitle, DrawerDescription, DrawerHeader, DrawerClose, DrawerFooter, DrawerContent, Drawer } from "@/components/ui/drawer"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible"
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"
import { ChevronRight, ChevronDown, Minus, Check } from "lucide-react"
import { cn } from "@/lib/utils"
import TextField from '@mui/material/TextField';
import { TableHead, TableRow, TableHeader, TableCell, TableBody, Table } from "@/components/ui/table"
import { useAuth, useUser, useClerk, useOrganization } from '@/auth/clerkShim'
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';
import { TableSkeleton } from '@/components/Page/PageSkeletons';
import adminStore from '@/store/adminStore';
import useTournamentStore from '@/store/tournamentStore';
import useLeagueStore from '@/store/leagueStore';
import useDivisionStore from '@/store/divisionStore';
import { toast } from "sonner";
import _ from 'lodash';
import moment from 'moment';
import helpers from '@/utils/helpers';

// ── ScopeTreeSelector ─────────────────────────────────────────────────────────

function ScopeCheckbox({ checked, onCheckedChange, disabled }) {
  return (
    <CheckboxPrimitive.Root
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      className={cn(
        "peer h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground"
      )}
    >
      <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
        {checked === 'indeterminate' ? <Minus className="h-3 w-3" /> : <Check className="h-3 w-3" />}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

function DivisionNode({ division, isEffectiveFn, isDisabledByParentFn, toggle }) {
  const checked = isEffectiveFn('division', division._id);
  const disabled = isDisabledByParentFn('division', division._id);
  return (
    <div className="flex items-center gap-1 py-0.5">
      <span className="h-4 w-4 flex-shrink-0" />
      <ScopeCheckbox
        checked={checked}
        onCheckedChange={() => toggle('division', division._id)}
        disabled={disabled}
      />
      <span className={cn("text-sm", disabled && "text-muted-foreground")}>{division.name}</span>
    </div>
  );
}

function LeagueNode({ league, isEffectiveFn, isIndeterminateFn, isDisabledByParentFn, toggle }) {
  const [open, setOpen] = useState(false);
  const checked = isEffectiveFn('league', league._id) ? true : isIndeterminateFn('league', league._id) ? 'indeterminate' : false;
  const disabled = isDisabledByParentFn('league', league._id);
  const childDivisions = league.children || [];

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="flex items-center gap-1 py-0.5">
        {childDivisions.length > 0 ? (
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="icon" className="h-4 w-4 p-0 flex-shrink-0">
              {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            </Button>
          </CollapsibleTrigger>
        ) : (
          <span className="h-4 w-4 flex-shrink-0" />
        )}
        <ScopeCheckbox
          checked={checked}
          onCheckedChange={() => toggle('league', league._id)}
          disabled={disabled}
        />
        <span className={cn("text-sm", disabled && "text-muted-foreground")}>{league.name}</span>
      </div>
      {childDivisions.length > 0 && (
        <CollapsibleContent>
          <div className="ml-5 space-y-0">
            {childDivisions.map((d) => (
              <DivisionNode
                key={d._id}
                division={d}
                isEffectiveFn={isEffectiveFn}
                isDisabledByParentFn={isDisabledByParentFn}
                toggle={toggle}
              />
            ))}
          </div>
        </CollapsibleContent>
      )}
    </Collapsible>
  );
}

function TournamentNode({ tournament, isEffectiveFn, isIndeterminateFn, isDisabledByParentFn, toggle }) {
  const [open, setOpen] = useState(false);
  const checked = isEffectiveFn('tournament', tournament._id) ? true : isIndeterminateFn('tournament', tournament._id) ? 'indeterminate' : false;
  const childLeagues = tournament.children || [];

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="flex items-center gap-1 py-0.5">
        {childLeagues.length > 0 ? (
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="icon" className="h-4 w-4 p-0 flex-shrink-0">
              {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            </Button>
          </CollapsibleTrigger>
        ) : (
          <span className="h-4 w-4 flex-shrink-0" />
        )}
        <ScopeCheckbox
          checked={checked}
          onCheckedChange={() => toggle('tournament', tournament._id)}
          disabled={false}
        />
        <span className="text-sm font-medium">{tournament.name}</span>
      </div>
      {childLeagues.length > 0 && (
        <CollapsibleContent>
          <div className="ml-5 space-y-0">
            {childLeagues.map((league) => (
              <LeagueNode
                key={league._id}
                league={league}
                isEffectiveFn={isEffectiveFn}
                isIndeterminateFn={isIndeterminateFn}
                isDisabledByParentFn={isDisabledByParentFn}
                toggle={toggle}
              />
            ))}
          </div>
        </CollapsibleContent>
      )}
    </Collapsible>
  );
}

function ScopeTreeSelector({ scopes, onChange, tournaments, leagues, divisions }) {
  function isEffective(level, id) {
    const sid = helpers.getObjId(id);
    if (scopes.some((s) => s.level === level && helpers.getObjId(s.refId) === sid)) return true;
    if (level === 'division') {
      const div = divisions.find((d) => helpers.getObjId(d._id) === sid);
      return div ? isEffective('league', div.league) : false;
    }
    if (level === 'league') {
      const lg = leagues.find((l) => helpers.getObjId(l._id) === sid);
      return lg?.tournament ? isEffective('tournament', lg.tournament) : false;
    }
    return false;
  }

  function isDisabledByParent(level, id) {
    if (level === 'tournament') return false;
    const sid = helpers.getObjId(id);
    if (level === 'league') {
      const lg = leagues.find((l) => helpers.getObjId(l._id) === sid);
      return !!(lg?.tournament && isEffective('tournament', lg.tournament));
    }
    if (level === 'division') {
      const div = divisions.find((d) => helpers.getObjId(d._id) === sid);
      return div ? isEffective('league', div.league) : false;
    }
    return false;
  }

  function isIndeterminate(level, id) {
    if (isEffective(level, id)) return false;
    const sid = helpers.getObjId(id);
    if (level === 'tournament') {
      return leagues.filter((l) => helpers.getObjId(l.tournament) === sid).some((l) => isEffective('league', l._id));
    }
    if (level === 'league') {
      return divisions.filter((d) => helpers.getObjId(d.league) === sid).some((d) => isEffective('division', d._id));
    }
    return false;
  }

  function toggle(level, id) {
    if (isDisabledByParent(level, id)) return;
    const sid = helpers.getObjId(id);
    const exists = scopes.some((s) => s.level === level && helpers.getObjId(s.refId) === sid);
    if (exists) {
      onChange(scopes.filter((s) => !(s.level === level && helpers.getObjId(s.refId) === sid)));
    } else {
      onChange([...scopes, { level, refId: sid }]);
    }
  }

  const tournamentedLeagues = leagues.filter((l) => l.tournament);
  const orphanLeagues = leagues.filter((l) => !l.tournament);
  const tree = tournaments.map((t) => ({
    ...t,
    children: tournamentedLeagues
      .filter((l) => helpers.getObjId(l.tournament) === helpers.getObjId(t._id))
      .map((l) => ({ ...l, children: divisions.filter((d) => helpers.getObjId(d.league) === helpers.getObjId(l._id)) })),
  }));

  return (
    <div className="space-y-1 max-h-80 overflow-y-auto pr-1">
      {tree.map((tournament) => (
        <TournamentNode
          key={tournament._id}
          tournament={tournament}
          isEffectiveFn={isEffective}
          isIndeterminateFn={isIndeterminate}
          isDisabledByParentFn={isDisabledByParent}
          toggle={toggle}
        />
      ))}
      {orphanLeagues.length > 0 && (
        <div className="mt-2">
          {tree.length > 0 && <p className="text-xs text-muted-foreground mb-1">Standalone Leagues</p>}
          {orphanLeagues.map((league) => (
            <LeagueNode
              key={league._id}
              league={{ ...league, children: divisions.filter((d) => helpers.getObjId(d.league) === helpers.getObjId(league._id)) }}
              isEffectiveFn={isEffective}
              isIndeterminateFn={isIndeterminate}
              isDisabledByParentFn={isDisabledByParent}
              toggle={toggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── EditScopesDialog ──────────────────────────────────────────────────────────

function EditScopesDialog({ member, onSave, tournaments, leagues, divisions, onClose }) {
  const adminDoc = member?.adminDoc;
  const [selectedRole, setSelectedRole] = useState(
    member?.role === 'org:admin' ? 'org:admin' : 'org:member'
  );
  const [scopes, setScopes] = useState(
    (adminDoc?.accessScopes || []).map((s) => ({ level: s.level, refId: s.refId?.toString() }))
  );

  const handleSave = () => {
    onSave(member, selectedRole, scopes);
    onClose();
  };

  return (
    <DialogContent className="sm:max-w-[480px]">
      <DialogHeader>
        <DialogTitle>Edit Permissions</DialogTitle>
        <DialogDescription>{member?.emailAddress}</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-2">
        <div>
          <p className="text-sm font-semibold mb-2">Role</p>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="role"
                value="org:admin"
                checked={selectedRole === 'org:admin'}
                onChange={() => setSelectedRole('org:admin')}
              />
              Super Admin
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="role"
                value="org:member"
                checked={selectedRole === 'org:member'}
                onChange={() => setSelectedRole('org:member')}
              />
              Limited Access
            </label>
          </div>
        </div>
        {selectedRole === 'org:member' && (
          <div>
            <p className="text-sm font-semibold mb-2">Access Scopes</p>
            <ScopeTreeSelector
              scopes={scopes}
              onChange={setScopes}
              tournaments={tournaments}
              leagues={leagues}
              divisions={divisions}
            />
          </div>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave}>Save</Button>
      </DialogFooter>
    </DialogContent>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function Component() {
  const { user, isLoaded: isLoadedUser } = useUser();
  const { getToken, orgSlug, ...rest } = useAuth();
  const { organization, isLoaded } = useOrganization();
  const [ memberships, setMemberships ] = useState([]);
  const [ invitations, setInvitations ] = useState([]);
  const [ editTarget, setEditTarget ] = useState(null);

  const [ inviteDialogOpen, setInviteDialogOpen ] = useState(false);

  // ?invite=1 deep-links straight into the invite dialog (the ⌘K palette's
  // "Invite admin" action). Effect-based so it works while already on this
  // page; param cleared inside the guard (replace:true) to avoid loops.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('invite')) {
      setInviteDialogOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [ email, setEmail ] = useState('');
  const [ firstName, setFirstName ] = useState('');
  const [ lastName, setLastName ] = useState('');
  const [ inviteRole, setInviteRole ] = useState('org:member');
  const [ inviteScopes, setInviteScopes ] = useState([]);

  // Skeleton only until the FIRST fetch batch resolves — memberships are local
  // state (no store cache), so never show the empty table while loading.
  const [loaded, setLoaded] = useState(false);

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const canSubmit = isValidEmail && (inviteRole === 'org:admin' || inviteScopes.length > 0);

  const role = _.get(user, 'organizationMemberships[0].role', 'org:member');
  const isAdmin = role === 'org:admin';

  const { postInvitation, getMemberships, getInvitations, revokeInvitation, removeMembership, promoteMembership, updateAdminScopes, getAdminsByClerkIds, adminsByClerkId } = adminStore((state: any) => ({
    revokeInvitation: state.revokeInvitation(getToken),
    removeMembership: state.removeMembership(getToken),
    postInvitation: state.postInvitation(getToken),
    getMemberships: state.getMemberships(getToken),
    getInvitations: state.getInvitations(getToken),
    promoteMembership: state.promoteMembership(getToken),
    updateAdminScopes: state.updateAdminScopes(getToken),
    getAdminsByClerkIds: state.getAdminsByClerkIds(getToken),
    adminsByClerkId: state.adminsByClerkId,
  }));

  const { tournaments, getTournamentsByLeagueName } = useTournamentStore((state) => ({
    tournaments: state.tournaments,
    getTournamentsByLeagueName: state.getTournamentsByLeagueName,
  }));
  const { leagues, getLeaguesByOrganizationName } = useLeagueStore((state) => ({
    leagues: state.leagues,
    getLeaguesByOrganizationName: state.getLeaguesByOrganizationName,
  }));
  const { divisions, getDivisionsByLeagueName } = useDivisionStore((state) => ({
    divisions: state.divisions,
    getDivisionsByLeagueName: state.getDivisionsByLeagueName,
  }));

  useEffect(() => {
    console.log('Component::user', user);
    console.log('Component::organization', organization);
    const fetches = [
      getMemberships(organization.id, (res) => {
        console.log('Component::getMemberships', res);
        setMemberships(res);
        // Load admin documents for all members
        const clerkIds = (res || []).map((m) => m.publicUserData?.userId).filter(Boolean);
        if (clerkIds.length > 0) {
          getAdminsByClerkIds(clerkIds);
        }
      }),
      getInvitations(organization.id, (res) => {
        console.log('Component::getInvitations', res);
        setInvitations(res);
      }),
    ];
    if (orgSlug) {
      fetches.push(
        getTournamentsByLeagueName({ leagueName: orgSlug }),
        getLeaguesByOrganizationName(orgSlug),
        getDivisionsByLeagueName(orgSlug),
      );
    }
    Promise.all(fetches.map((p) => Promise.resolve(p))).finally(() => setLoaded(true));
  }, [isLoaded]);
  console.log('Component::memberships', memberships);
  console.log('Component::invitations', invitations);
  console.log('Component::user', user);

  // Resolve scope label from resource data
  const resolveScopeLabel = (scope) => {
    const id = scope.refId?.toString();
    if (scope.level === 'tournament') {
      const t = tournaments.find((t) => t._id === id);
      return `tournament: ${t?.name || id}`;
    }
    if (scope.level === 'league') {
      const l = leagues.find((l) => l._id === id);
      return `league: ${l?.name || id}`;
    }
    if (scope.level === 'division') {
      const d = divisions.find((d) => d._id === id);
      return `division: ${d?.name || id}`;
    }
    return `${scope.level}: ${id}`;
  };

  // Refetch BOTH lists after an invite. Which list the invitee lands in
  // depends on server state the client can't know: an email that already has
  // an admin account (password set) is filed straight under MEMBERSHIPS, not
  // invitations — refetching only invitations left that row invisible until a
  // manual reload (owner-reported 2026-09-27).
  const refreshLists = () => {
    getInvitations(organization.id, (res) => {
      console.log('Component::getInvitations', res);
      setInvitations(res);
    });
    getMemberships(organization.id, (res) => {
      console.log('Component::getMemberships', res);
      setMemberships(res);
      const clerkIds = (res || []).map((m) => m.publicUserData?.userId).filter(Boolean);
      if (clerkIds.length > 0) getAdminsByClerkIds(clerkIds);
    });
  };

  const onSubmit = async () => {
    console.log('onSubmit::email', email, organization);
    await postInvitation(
      email,
      organization.id,
      user.id,
      firstName,
      lastName,
      (res) => {
        console.log('onSubmit::res', res);
        refreshLists();
      },
      inviteRole === 'org:member' ? inviteScopes : [],
      inviteRole,
    );
    setInviteDialogOpen(false);
  };

  const reSubmit = async (emailAddress) => {
    console.log('reSubmit::email', email, organization, user);
    const hasConfirmed = window.confirm(`Are you sure you want to resend this invitation?`);
    if (!hasConfirmed) return;
    postInvitation(emailAddress, organization.id, user.id, firstName, lastName, (res) => {
      console.log('reSubmit::res', res);
      refreshLists();
    });
    toast.success(`Invitation sent`);
  }

  const onRevoke = async (orgId, invitationId) => {
    const invitation = {
      clerkId: user.id,
      organization: orgId,
      invitationId
    };
    console.log('onRevoke::invitation', invitation);
    const hasConfirmed = window.confirm(`Are you sure you want to revoke this invitation?`);
    if (!hasConfirmed) return;
    revokeInvitation(invitation, (res) => {
      console.log('onRevoke::res', res);
      getInvitations(organization.id, (res2) => {
        console.log('Component::getInvitations', res2);
        setInvitations(res2);
      });
    });
  };

  const onRemove = async (userId, organizationId, emailAddress) => {
    const membership = {
      userId,
      organization: organizationId,
      leagueName: orgSlug,
      emailAddress
    };
    console.log('onRemove::membership', membership);
    const hasConfirmed = window.confirm(`Are you sure you want to remove ${emailAddress} from the organization?`);
    if (!hasConfirmed) return;
    removeMembership(membership, (res) => {
      console.log('onRemove::res', res);
      getMemberships(organizationId, (res2) => {
        console.log('Component::getMemberships', res2);
        setMemberships(res2);
      });
    });
  }

  const onPromote = async (userId, organizationId, emailAddress, currentRole) => {
    const newRole = currentRole === 'org:member' ? 'org:admin' : 'org:member';
    const action = newRole === 'org:admin' ? 'promote' : 'demote';
    const hasConfirmed = window.confirm(`Are you sure you want to ${action} ${emailAddress} to ${newRole}?`);
    if (!hasConfirmed) return;

    const membershipData = {
      userId,
      organizationId,
      role: newRole
    };

    promoteMembership(membershipData, (res) => {
      console.log('onPromote::res', res);
      getMemberships(organizationId, (res2) => {
        console.log('Component::getMemberships', res2);
        setMemberships(res2);
      });
    });
  }

  const onSavePermissions = async (member, newRole, newScopes) => {
    const { userId, organization: orgId, role: currentRole } = member;

    // Update role if changed
    if (newRole !== currentRole) {
      await new Promise((resolve) => {
        promoteMembership({ userId, organizationId: orgId, role: newRole }, resolve);
      });
    }

    // Update scopes (only relevant for org:member)
    updateAdminScopes(userId, newRole === 'org:admin' ? [] : newScopes, () => {
      getMemberships(orgId, (res) => {
        setMemberships(res);
        const clerkIds = (res || []).map((m) => m.publicUserData?.userId).filter(Boolean);
        if (clerkIds.length > 0) getAdminsByClerkIds(clerkIds);
      });
    });
  };

  const membershipList = _.map(memberships, ({ publicUserData, id, role, organization, lastActiveAt, lastSignInAt }) => {
    const userId = publicUserData.userId;
    return {
      id,
      role,
      emailAddress: publicUserData.identifier,
      organization: organization.id,
      userId,
      adminDoc: adminsByClerkId?.[userId],
      lastActiveAt,
      lastSignInAt,
    };
  });

  const invitationList = _.filter(invitations, ({ emailAddress, role, status }) => {
    if (status === 'accepted') return false;
    if (status === 'revoked') return false;
    const found = _.find(memberships, { email: emailAddress });
    if (found) return false;
    return true;
  })

  const onSubmit2 = async () => {};


  return (
    <PageContainer>
      <PageHeader
        title="Users"
        actions={(
        <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
          <DialogTrigger asChild>
            <Button disabled={!isAdmin} size="sm">Invite to Organization</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[480px]">
            <DialogHeader>
              <DialogTitle>Invite to Organization</DialogTitle>
              <DialogDescription>Enter an email address to send an invite to join the organization.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right" htmlFor="firstName">
                  First Name
                </Label>
                <TextField
                  className="col-span-3"
                  hiddenLabel
                  id="firstName"
                  size="small"
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First Name"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right" htmlFor="lastName">
                  Last Name
                </Label>
                <TextField
                  className="col-span-3"
                  hiddenLabel
                  id="lastName"
                  size="small"
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last Name"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right" htmlFor="email">
                  Email
                </Label>
                <TextField
                  className="col-span-3"
                  hiddenLabel
                  id="email"
                  size="small"
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="example@email.com"
                />
              </div>
              <div className="grid grid-cols-4 items-start gap-4">
                <Label className="text-right pt-1">Role</Label>
                <div className="col-span-3 space-y-1">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="inviteRole"
                      value="org:admin"
                      checked={inviteRole === 'org:admin'}
                      onChange={() => setInviteRole('org:admin')}
                    />
                    Super Admin
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="inviteRole"
                      value="org:member"
                      checked={inviteRole === 'org:member'}
                      onChange={() => setInviteRole('org:member')}
                    />
                    Limited Access
                  </label>
                </div>
              </div>
              {inviteRole === 'org:member' && (
                <div className="grid grid-cols-4 items-start gap-4">
                  <Label className="text-right pt-1">Scopes</Label>
                  <div className="col-span-3">
                    <ScopeTreeSelector
                      scopes={inviteScopes}
                      onChange={setInviteScopes}
                      tournaments={tournaments}
                      leagues={leagues}
                      divisions={divisions}
                    />
                    {inviteScopes.length === 0 && (
                      <p className="text-sm text-destructive mt-1">Select at least one scope</p>
                    )}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="submit" disabled={!canSubmit} onClick={onSubmit}>Send Invite</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        )}
      />
      {!loaded && membershipList.length === 0 && invitationList.length === 0 ? (
        <TableSkeleton columns={5} />
      ) : (
      <div className="border shadow-sm rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Permissions</TableHead>
              <TableHead>Last Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {membershipList.map(({ emailAddress, id, role, organization, userId, adminDoc, lastActiveAt, lastSignInAt }) => {
              const isCurrentUser = userId === user.id;
              const canPromote = isAdmin && !isCurrentUser;
              const canEditPerms = isAdmin && !isCurrentUser;
              const buttonText = role === 'org:member' ? 'Promote to Admin' : 'Demote to Member';
              const memberScopes = adminDoc?.accessScopes || [];
              const lastActiveTs = lastActiveAt ?? lastSignInAt;

              return (
                <TableRow key={id}>
                  <TableCell>{emailAddress}</TableCell>
                  <TableCell>{role}</TableCell>
                  <TableCell>
                    {role === 'org:admin' ? (
                      <Badge variant="default">Super Admin</Badge>
                    ) : memberScopes.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {memberScopes.map((scope, i) => (
                          <Badge key={i} variant="secondary">{resolveScopeLabel(scope)}</Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">No scopes</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {lastActiveTs ? (
                      <span className="text-sm" title={moment(lastActiveTs).format('MMM D, YYYY h:mm A')}>
                        {moment(lastActiveTs).fromNow()}
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-sm">Never</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      {isAdmin && (
                        <Dialog open={editTarget?.userId === userId} onOpenChange={(open) => { if (!open) setEditTarget(null); }}>
                          <DialogTrigger asChild>
                            <Button
                              disabled={!canEditPerms}
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditTarget({ emailAddress, id, role, organization, userId, adminDoc });
                              }}
                            >
                              Edit Permissions
                            </Button>
                          </DialogTrigger>
                          {editTarget?.userId === userId && (
                            <EditScopesDialog
                              member={editTarget}
                              onSave={onSavePermissions}
                              tournaments={tournaments}
                              leagues={leagues}
                              divisions={divisions}
                              onClose={() => setEditTarget(null)}
                            />
                          )}
                        </Dialog>
                      )}
                      <Button
                        disabled={!canPromote}
                        size="sm"
                        variant="outline"
                        onClick={() => { onPromote(userId, organization, emailAddress, role)}}
                      >
                        {buttonText}
                      </Button>
                      <Button
                        disabled={!isAdmin}
                        size="sm"
                        variant="outline"
                        onClick={() => { onRemove(userId, organization, emailAddress)}}
                      >
                        Remove
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {invitationList.map(({ emailAddress, id, role, createdAt }) => {
              return (
                <TableRow key={id}>
                  <TableCell>{emailAddress}</TableCell>
                  <TableCell>{role}</TableCell>
                  <TableCell>
                  </TableCell>
                  <TableCell>
                    <span className="text-muted-foreground text-sm">—</span>
                  </TableCell>
                  <TableCell className="text-right">

                      <span style={{ padding: '5px'}}>Invitation sent on {moment(createdAt).format('MM/DD/YYYY')}</span>
                      <Button size="sm" disabled={!isAdmin} variant="outline" onClick={() => { onRevoke(organization.id, id)}}>
                        Revoke Invite
                      </Button>
                      <Button size="sm" disabled={!isAdmin} variant="outline" onClick={() => { reSubmit(emailAddress)}}>
                        Resend Invite
                      </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      )}
    </PageContainer>
  )
}

function BellIcon(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  )
}


function HomeIcon(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  )
}


function LineChartIcon(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 3v18h18" />
      <path d="m19 9-5 5-4-4-3 3" />
    </svg>
  )
}


function Package2Icon(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 9h18v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9Z" />
      <path d="m3 9 2.45-4.9A2 2 0 0 1 7.24 3h9.52a2 2 0 0 1 1.8 1.1L21 9" />
      <path d="M12 3v6" />
    </svg>
  )
}


function PackageIcon(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m7.5 4.27 9 5.15" />
      <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
      <path d="m3.3 7 8.7 5 8.7-5" />
      <path d="M12 22V12" />
    </svg>
  )
}


function SearchIcon(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  )
}


function ShoppingCartIcon(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="8" cy="21" r="1" />
      <circle cx="19" cy="21" r="1" />
      <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
    </svg>
  )
}


function UsersIcon(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  )
}
